import React, { useState, useEffect } from 'react';
import { useToast } from '../../context/ToastContext';

import { buildApiUrl } from '../../config/api';
import { fetchJson } from '../../utils/fetchJson';
import Modal from '../../components/Modal';
import AdminLayout from '../../components/admin/AdminLayout';

const DeletionRequests = () => {
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const token = localStorage.getItem('token');
    const toast = useToast();

    // Modal State
    const [isConfirmOpen, setIsConfirmOpen] = useState(false);
    const [userToDelete, setUserToDelete] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);

    const fetchRequests = React.useCallback(async () => {
        try {
            const { response, data } = await fetchJson(buildApiUrl('/api/deletion/admin/requests'), {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.ok) {
                setRequests(data);
            } else {
                throw new Error(data.message || 'Failed to fetch');
            }
            setLoading(false);
        } catch (error) {
            console.error('Error fetching deletion requests:', error);
            setLoading(false);
            toast.error('Failed to load deletion requests');
        }
    }, [token, toast]);

    useEffect(() => {
        fetchRequests();
    }, [fetchRequests]);

    const initiateDeletion = (request) => {
        setUserToDelete(request);
        setIsConfirmOpen(true);
    };

    const handleExecuteDeletion = async () => {
        if (!userToDelete) return;
        setIsDeleting(true);

        try {
            const { response, data } = await fetchJson(buildApiUrl(`/api/deletion/admin/execute/${userToDelete._id}`), {
                method: 'DELETE',
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.ok) {
                toast.success(`User ${userToDelete.email} deleted successfully`);
                fetchRequests(); // Refresh list
                setIsConfirmOpen(false);
                setUserToDelete(null);
            } else {
                throw new Error(data.message || 'Failed to delete');
            }
        } catch (error) {
            console.error('Error executing deletion:', error);
            toast.error(error.message || 'Failed to delete user');
        } finally {
            setIsDeleting(false);
        }
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleString();
    };

    const getStatusBadge = (status) => {
        switch (status) {
            case 'verified':
                return <span className="bg-green-100 text-green-800 text-xs font-semibold mr-2 px-2.5 py-0.5 rounded">Verified (Ready to Delete)</span>;
            case 'pending':
                return <span className="bg-yellow-100 text-yellow-800 text-xs font-semibold mr-2 px-2.5 py-0.5 rounded">Pending Verification</span>;
            case 'completed':
                return <span className="bg-gray-100 text-gray-800 text-xs font-semibold mr-2 px-2.5 py-0.5 rounded">Completed</span>;
            default:
                return <span className="bg-blue-100 text-blue-800 text-xs font-semibold mr-2 px-2.5 py-0.5 rounded">{status}</span>;
        }
    };

    return (
        <AdminLayout>
            <div className="p-6">
                <h1 className="text-2xl font-bold mb-6 text-gray-800">Account Deletion Requests</h1>

                <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Requested At</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Verified At</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {loading ? (
                                    <tr>
                                        <td colSpan="5" className="px-6 py-10 text-center text-gray-500">
                                            Loading requests...
                                        </td>
                                    </tr>
                                ) : requests.length === 0 ? (
                                    <tr>
                                        <td colSpan="5" className="px-6 py-10 text-center text-gray-500">
                                            No deletion requests found.
                                        </td>
                                    </tr>
                                ) : (
                                    requests.map((request) => (
                                        <tr key={request._id} className="hover:bg-gray-50">
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{request.email}</td>
                                            <td className="px-6 py-4 whitespace-nowrap">{getStatusBadge(request.status)}</td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatDate(request.requestedAt)}</td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                {request.verifiedAt ? formatDate(request.verifiedAt) : '-'}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                                {request.status === 'verified' && (
                                                    <button
                                                        onClick={() => initiateDeletion(request)}
                                                        className="text-white bg-red-600 hover:bg-red-700 px-3 py-1 rounded-md text-sm transition-colors"
                                                    >
                                                        Delete User
                                                    </button>
                                                )}
                                                {request.status === 'completed' && (
                                                    <span className="text-gray-400 cursor-not-allowed">Deleted</span>
                                                )}
                                                {request.status === 'pending' && (
                                                    <span className="text-gray-400 italic">User must verify email</span>
                                                )}
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Confirmation Modal */}
                <Modal isOpen={isConfirmOpen} onClose={() => !isDeleting && setIsConfirmOpen(false)} size="sm">
                    <div className="p-6 text-center">
                        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path>
                            </svg>
                        </div>
                        <h3 className="text-xl font-bold text-gray-900 mb-2">Delete User?</h3>
                        <p className="text-gray-600 mb-6">
                            Are you sure you want to <strong>permanently delete</strong> the user <span className="font-semibold text-gray-900">{userToDelete?.email}</span>?
                            <br /><br />
                            This action cannot be undone. All profile data and messages will be erased.
                        </p>
                        <div className="flex justify-center space-x-4">
                            <button
                                onClick={() => setIsConfirmOpen(false)}
                                disabled={isDeleting}
                                className={`px-4 py-2 rounded-lg transition-colors ${isDeleting ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-gray-200 text-gray-800 hover:bg-gray-300'}`}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleExecuteDeletion}
                                disabled={isDeleting}
                                className={`flex items-center px-4 py-2 rounded-lg transition-colors ${isDeleting ? 'bg-red-400 cursor-not-allowed' : 'bg-red-600 hover:bg-red-700'} text-white`}
                            >
                                {isDeleting ? (
                                    <>
                                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        Deleting...
                                    </>
                                ) : (
                                    "Yes, I'm sure"
                                )}
                            </button>
                        </div>
                    </div>
                </Modal>
            </div>
        </AdminLayout>
    );
};

export default DeletionRequests;
